import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Form, Modal, Table, Badge, Spinner } from 'react-bootstrap';
import { FaUser, FaPlus, FaEdit, FaTrash, FaSearch, FaUserShield, FaUserCog, FaEye, FaSync } from 'react-icons/fa';
import { GetFromApi, PostToApi, PutToApi, DeleteFromApi } from '../utils/api';
import '../styles/Pages.css';

const SubUsers = () => {
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [selectedUser, setSelectedUser] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState([]);
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', password: '', role: 'viewer',
    permissions: { viewVehicles: true, viewReports: false, viewAlerts: false, manageGeofence: false, manageUsers: false },
    is_active: true
  });

  const roles = [
    { id: 'admin', name: 'Admin', icon: FaUserShield, color: '#A855F7' },
    { id: 'manager', name: 'Manager', icon: FaUserCog, color: '#3B82F6' },
    { id: 'viewer', name: 'Viewer', icon: FaEye, color: '#22C55E' }
  ];

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await GetFromApi('/users');
      if (response.success) setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); }, []);

  const filteredUsers = users.filter(user => {
    const matchSearch = user.name?.toLowerCase().includes(searchTerm.toLowerCase()) || user.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchRole = filterRole === 'all' || user.role === filterRole;
    return matchSearch && matchRole;
  });

  const getRoleInfo = (roleId) => roles.find(r => r.id === roleId) || roles[2];

  const handleOpenModal = (user = null) => {
    if (user) {
      setEditMode(true);
      setFormData({ name: user.name, email: user.email, phone: user.phone, password: '', role: user.role, permissions: user.permissions || {}, is_active: user.is_active });
      setSelectedUser(user);
    } else {
      setEditMode(false);
      setFormData({ name: '', email: '', phone: '', password: '', role: 'viewer', permissions: { viewVehicles: true, viewReports: false, viewAlerts: false, manageGeofence: false, manageUsers: false }, is_active: true });
      setSelectedUser(null);
    }
    setShowModal(true);
  };

  const handleCloseModal = () => { setShowModal(false); setSelectedUser(null); setEditMode(false); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editMode && selectedUser) {
        await PutToApi(`/users/${selectedUser.id}`, formData);
      } else {
        await PostToApi('/users', formData);
      }
      await fetchUsers();
      handleCloseModal();
    } catch (error) {
      console.error('Error saving user:', error);
      alert('Error saving user: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await DeleteFromApi(`/users/${id}`);
        await fetchUsers();
      } catch (error) {
        console.error('Error deleting user:', error);
      }
    }
  };

  const handleToggleActive = async (id) => {
    try {
      await PutToApi(`/users/${id}/toggle-active`, {});
      await fetchUsers();
    } catch (error) {
      console.error('Error toggling user status:', error);
    }
  };

  const handlePermissionChange = (permission) => {
    setFormData({ ...formData, permissions: { ...formData.permissions, [permission]: !formData.permissions[permission] } });
  };

  const getInitials = (name) => name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U';

  return (
    <Container fluid className="p-3">
      <div className="page-header">
        <h4 className="page-title">Sub Users Management</h4>
        <div className="d-flex gap-2">
          <Button variant="light" onClick={fetchUsers} disabled={loading} style={{ borderRadius: '8px' }}>{loading ? <Spinner size="sm" /> : <FaSync />}</Button>
          <Button variant="primary" onClick={() => handleOpenModal()} style={{ borderRadius: '8px' }}><FaPlus className="me-2" /> Add User</Button>
        </div>
      </div>

      <Row className="mb-4">
        <Col md={3} sm={6} className="mb-3">
          <Card className="stats-card border-0 shadow-sm">
            <div className="d-flex align-items-center">
              <div className="icon-box me-3" style={{ backgroundColor: 'rgba(59, 130, 246, 0.1)', color: '#3B82F6' }}><FaUser /></div>
              <div><div className="stats-value">{users.length}</div><div className="stats-label">Total Users</div></div>
            </div>
          </Card>
        </Col>
        {roles.map(role => {
          const count = users.filter(u => u.role === role.id).length;
          const IconComponent = role.icon;
          return (
            <Col key={role.id} md={3} sm={6} className="mb-3">
              <Card className={`stats-card border-0 shadow-sm ${filterRole === role.id ? 'border border-2 border-primary' : ''}`}
                style={{ cursor: 'pointer' }} onClick={() => setFilterRole(filterRole === role.id ? 'all' : role.id)}>
                <div className="d-flex align-items-center">
                  <div className="icon-box me-3" style={{ backgroundColor: `${role.color}20`, color: role.color }}><IconComponent /></div>
                  <div><div className="stats-value">{count}</div><div className="stats-label">{role.name}s</div></div>
                </div>
              </Card>
            </Col>
          );
        })}
      </Row>

      <Card className="page-card">
        <Card.Header className="bg-white border-bottom d-flex justify-content-between align-items-center">
          <h6 className="mb-0 fw-semibold text-dark">Users List</h6>
          <div className="d-flex gap-2">
            <div className="search-input-wrapper">
              <FaSearch className="search-icon" />
              <Form.Control type="text" placeholder="Search users..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                style={{ paddingLeft: '2.5rem', borderRadius: '8px', width: '250px' }} />
            </div>
            <Form.Select value={filterRole} onChange={(e) => setFilterRole(e.target.value)} style={{ width: '150px', borderRadius: '8px' }}>
              <option value="all">All Roles</option>
              {roles.map(role => <option key={role.id} value={role.id}>{role.name}</option>)}
            </Form.Select>
          </div>
        </Card.Header>
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
          ) : (
            <Table hover responsive className="mb-0">
              <thead style={{ backgroundColor: '#f8fafc' }}><tr><th>User</th><th>Contact</th><th>Role</th><th>Permissions</th><th>Status</th><th>Actions</th></tr></thead>
              <tbody>
                {filteredUsers.map(user => {
                  const roleInfo = getRoleInfo(user.role);
                  const IconComponent = roleInfo.icon;
                  return (
                    <tr key={user.id}>
                      <td><div className="d-flex align-items-center"><div className="user-avatar me-2">{getInitials(user.name)}</div><span className="fw-semibold">{user.name}</span></div></td>
                      <td><div>{user.email}</div><small className="text-muted">{user.phone}</small></td>
                      <td><Badge style={{ backgroundColor: `${roleInfo.color}20`, color: roleInfo.color }} className="d-flex align-items-center gap-1 px-2 py-1"><IconComponent size={12} /> {roleInfo.name}</Badge></td>
                      <td>
                        <div className="d-flex flex-wrap gap-1">
                          {user.permissions?.viewVehicles && <Badge bg="light" text="dark" className="small">Vehicles</Badge>}
                          {user.permissions?.viewReports && <Badge bg="light" text="dark" className="small">Reports</Badge>}
                          {user.permissions?.viewAlerts && <Badge bg="light" text="dark" className="small">Alerts</Badge>}
                        </div>
                      </td>
                      <td><Form.Check type="switch" checked={user.is_active} onChange={() => handleToggleActive(user.id)} label={user.is_active ? 'Active' : 'Inactive'} /></td>
                      <td>
                        <button className="action-btn edit me-1" onClick={() => handleOpenModal(user)}><FaEdit size={14} /></button>
                        <button className="action-btn delete" onClick={() => handleDelete(user.id)}><FaTrash size={14} /></button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
          {filteredUsers.length === 0 && !loading && (
            <div className="empty-state py-5"><div className="empty-state-icon"><FaUser /></div><div className="empty-state-title">No users found</div></div>
          )}
        </Card.Body>
      </Card>

      <Modal show={showModal} onHide={handleCloseModal} centered className="modal-custom" size="lg">
        <Modal.Header closeButton><Modal.Title>{editMode ? 'Edit User' : 'Add New User'}</Modal.Title></Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Row>
              <Col md={6}><Form.Group className="mb-3"><Form.Label className="form-label-custom">Full Name</Form.Label><Form.Control type="text" className="form-control-custom" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required /></Form.Group></Col>
              <Col md={6}><Form.Group className="mb-3"><Form.Label className="form-label-custom">Email</Form.Label><Form.Control type="email" className="form-control-custom" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} required /></Form.Group></Col>
            </Row>
            <Row>
              <Col md={6}><Form.Group className="mb-3"><Form.Label className="form-label-custom">Phone</Form.Label><Form.Control type="tel" className="form-control-custom" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} /></Form.Group></Col>
              <Col md={6}><Form.Group className="mb-3"><Form.Label className="form-label-custom">Role</Form.Label><Form.Select className="form-control-custom" value={formData.role} onChange={(e) => setFormData({...formData, role: e.target.value})}>{roles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}</Form.Select></Form.Group></Col>
            </Row>
            {!editMode && (
              <Form.Group className="mb-3"><Form.Label className="form-label-custom">Password</Form.Label><Form.Control type="password" className="form-control-custom" value={formData.password} onChange={(e) => setFormData({...formData, password: e.target.value})} required={!editMode} /></Form.Group>
            )}
            <Form.Group className="mb-3">
              <Form.Label className="form-label-custom">Permissions</Form.Label>
              <div className="d-flex flex-wrap gap-3 p-3 rounded" style={{ backgroundColor: '#f8fafc' }}>
                <Form.Check type="checkbox" label="View Vehicles" checked={formData.permissions.viewVehicles} onChange={() => handlePermissionChange('viewVehicles')} />
                <Form.Check type="checkbox" label="View Reports" checked={formData.permissions.viewReports} onChange={() => handlePermissionChange('viewReports')} />
                <Form.Check type="checkbox" label="View Alerts" checked={formData.permissions.viewAlerts} onChange={() => handlePermissionChange('viewAlerts')} />
                <Form.Check type="checkbox" label="Manage Geofence" checked={formData.permissions.manageGeofence} onChange={() => handlePermissionChange('manageGeofence')} />
                <Form.Check type="checkbox" label="Manage Users" checked={formData.permissions.manageUsers} onChange={() => handlePermissionChange('manageUsers')} />
              </div>
            </Form.Group>
            <Form.Check type="switch" label="Active User" checked={formData.is_active} onChange={(e) => setFormData({...formData, is_active: e.target.checked})} />
          </Modal.Body>
          <Modal.Footer>
            <Button variant="light" onClick={handleCloseModal}>Cancel</Button>
            <Button variant="primary" type="submit" disabled={saving}>{saving ? <Spinner size="sm" className="me-2" /> : null}{editMode ? 'Save Changes' : 'Add User'}</Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
};

export default SubUsers;
